import { ReactNode } from 'react';
import CountUp from 'react-countup';
import { ArrowLeft, Link2, ShieldX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import '../../design-system/index.css';
import './dnsActivityLayout.scss';

export type DnsRange = '1d' | '7d' | '30d';

// Display labels are resolved at render time (see rangeLabel) so they follow the active language
const rangeOptions: { value: DnsRange; days: number }[] = [
  { value: '1d', days: 1 },
  { value: '7d', days: 7 },
  { value: '30d', days: 30 },
];

type Props = {
  darkMode?: boolean;
  selectedRange: string;
  onRangeChange: (value: DnsRange) => void;
  uniqueVisited?: number;
  uniqueBlocked?: number;
  /** Hides the left panel (legacy map-marker drill-in) */
  hideSites?: boolean;
  /** Shows "Back to all activity" above the activity table */
  showBack?: boolean;
  onBack?: () => void;
  blockedTable: ReactNode;
  activityTable: ReactNode;
};

/**
 * Router / device DNS activity tab: range + unique-site stats + blocked-sites table
 * on the left, full DNS activity table on the right. Self-contained design-system
 * surface, so it themes correctly inside legacy (non-.bd) parent cards.
 */
export const DnsActivityLayout = (props: Props) => {
  const { t } = useTranslation();
  const rangeLabel = (days: number) =>
    days === 1 ? t('widgets.dnsActivity.today') : t('widgets.dnsActivity.pastDays', { count: days });

  return (
  <div className="bd dns-activity" data-theme={props.darkMode ? 'dark' : 'light'} data-accent="mint">
    <div className={`dna-grid ${props.hideSites ? 'single' : ''}`}>
      {!props.hideSites && (
        <aside className="bd-card dna-panel">
          <div className="dna-panel-hd">
            <span className="bd-field-label">{t('widgets.dnsActivity.historyLabel')}</span>
            <div className="bd-segmented dna-range" role="group" aria-label={t('widgets.dnsActivity.rangeAriaLabel')}>
              {rangeOptions.map((range) => (
                <button
                  key={range.value}
                  type="button"
                  className={props.selectedRange === range.value ? 'active' : ''}
                  aria-pressed={props.selectedRange === range.value}
                  onClick={() => props.onRangeChange(range.value)}
                >
                  {rangeLabel(range.days)}
                </button>
              ))}
            </div>
          </div>

          <div className="dna-stats">
            <div className="dna-stat">
              <span className="dna-stat-icon">
                <Link2 size={16} />
              </span>
              <span className="bd-meta">{t('widgets.dnsActivity.uniqueVisited')}</span>
              <CountUp className="bd-stat-num dna-stat-num" preserveValue end={Number(props.uniqueVisited) || 0} duration={1.5} separator="," />
            </div>
            <div className="dna-stat danger">
              <span className="dna-stat-icon">
                <ShieldX size={16} />
              </span>
              <span className="bd-meta">{t('widgets.dnsActivity.uniqueBlocked')}</span>
              <CountUp className="bd-stat-num dna-stat-num" preserveValue end={Number(props.uniqueBlocked) || 0} duration={1.5} separator="," />
            </div>
          </div>

          <div className="dna-table">{props.blockedTable}</div>
        </aside>
      )}

      <section className="bd-card dna-main">
        {props.showBack && (
          <button type="button" className="bd-pill dna-back" onClick={props.onBack}>
            <ArrowLeft size={14} />
            {t('widgets.dnsActivity.back')}
          </button>
        )}
        <div className="dna-table">{props.activityTable}</div>
      </section>
    </div>
  </div>
  );
};
